import React from 'react';
import { Badge } from '@shopify/polaris';
import type { Tier } from '../types';

/**
 * T033: TierBadge — displays the current subscription tier
 * as a colour-coded Polaris Badge with icon.
 *
 * Colour mapping:
 *  - FREE    → subdued (neutral)  🌱
 *  - STARTER → info (teal)        ⚡
 *  - GROWTH  → success (green)    🚀
 *  - SCALE   → attention (gold)   👑
 */

export const TIER_ICONS: Record<Tier, string> = {
  FREE: '🌱',
  STARTER: '⚡',
  GROWTH: '🚀',
  SCALE: '👑',
};

/** Returns the emoji icon for a given tier. */
export function getTierIcon(tier: Tier): string {
  return TIER_ICONS[tier] ?? '🌱';
}

const TIER_DISPLAY: Record<Tier, { label: string; tone: React.ComponentProps<typeof Badge>['tone'] }> = {
  FREE: { label: 'Free', tone: undefined },
  STARTER: { label: 'Starter', tone: 'info' },
  GROWTH: { label: 'Growth', tone: 'success' },
  SCALE: { label: 'Scale', tone: 'attention' },
};

export interface TierBadgeProps {
  /** Current subscription tier */
  tier: Tier;
  /** Optional size override */
  size?: 'small' | 'medium' | 'large';
  /** Show icon prefix (default: true) */
  showIcon?: boolean;
}

export function TierBadge({ tier, size, showIcon = true }: TierBadgeProps): React.JSX.Element {
  const config = TIER_DISPLAY[tier] ?? TIER_DISPLAY.FREE;
  const icon = TIER_ICONS[tier];
  return (
    <Badge tone={config.tone} size={size}>
      {showIcon ? `${icon} ${config.label}` : config.label}
    </Badge>
  );
}

export default TierBadge;
